import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";


const SERVICE_LABELS = {
  plomeria: "Plomería", electricidad: "Electricidad", gasista: "Gasista",
  pintura: "Pintura", albanileria: "Albañilería", cerrajeria: "Cerrajería",
  limpieza: "Limpieza", jardineria: "Jardinería",
};

const REQUEST_STATUS = {
  waiting_quotes: { label: "Esperando presupuestos", color: "#b06a00", bg: "#fff3e0" },
  quoted:         { label: "Presupuestos recibidos", color: "#163244", bg: "#e7f0f5" },
  assigned:       { label: "Prestador asignado",     color: "#1b7f4e", bg: "#e8f7ef" },
  cancelled:      { label: "Cancelada",              color: "#c0392b", bg: "#fdecea" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatARS(amount) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency", currency: "ARS", maximumFractionDigits: 0,
  }).format(amount);
}

function remainingTime(dateStr) {
  const diff = new Date(dateStr) - Date.now();
  if (diff <= 0) return null;
  const hrs  = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hrs > 0 ? `${hrs}h ${mins}min` : `${mins}min`;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function StarRating({ rating }) {
  return (
    <span style={{ fontSize: 12, color: "#E7A432", fontWeight: 700 }}>
      {"★".repeat(Math.round(rating))}{"☆".repeat(5 - Math.round(rating))}
      <span style={{ color: "#5c7a8a", marginLeft: 4 }}>{rating}</span>
    </span>
  );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────
function ConfirmModal({ quote, action, onConfirm, onClose }) {
  const isAccept = action === "accept";
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.55)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "32px",
        width: "min(440px, 92vw)",
        boxShadow: "0 8px 40px rgba(22,50,68,0.2)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%", margin: "0 auto 16px",
            background: isAccept ? "#e8f7ef" : "#fdecea",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28,
          }}>
            {isAccept ? "✓" : "✕"}
          </div>
          <h3 style={{ margin: "0 0 8px", color: "#163244", fontSize: 18, fontWeight: 800 }}>
            {isAccept ? "¿Aceptar este presupuesto?" : "¿Rechazar este presupuesto?"}
          </h3>
          <p style={{ margin: 0, color: "#5c7a8a", fontSize: 14, lineHeight: 1.6 }}>
            {isAccept
              ? <>Vas a contratar a <strong>{quote.provider_name}</strong> por <strong>{formatARS(quote.amount)}</strong>. Los demás presupuestos serán rechazados automáticamente.</>
              : <>El presupuesto de <strong>{quote.provider_name}</strong> será rechazado. Podés seguir viendo los otros.</>
            }
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "12px", borderRadius: 8,
              border: "2px solid #e0e9ef", background: "transparent",
              color: "#5c7a8a", fontFamily: "Montserrat, sans-serif",
              fontWeight: 600, fontSize: 14, cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, padding: "12px", borderRadius: 8, border: "none",
              background: isAccept ? "#163244" : "#c0392b",
              color: "#fff", fontFamily: "Montserrat, sans-serif",
              fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >
            {isAccept ? "Sí, contratar" : "Sí, rechazar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Quote Card ───────────────────────────────────────────────────────────────
function QuoteCard({ quote, rank, onAccept, onReject, disabled, loading }) {
  const [expanded, setExpanded] = useState(false);
  const remaining = remainingTime(quote.valid_until);
  const isAccepted = quote.status === "accepted";
  const isRejected = quote.status === "rejected";
  const isBest     = rank === 0;

  return (
    <div style={{
      background: "#fff", borderRadius: 14, marginBottom: 16,
      border: `2px solid ${isAccepted ? "#1b7f4e" : isBest && !isRejected ? "#E7A432" : "#e0e9ef"}`,
      boxShadow: isAccepted
        ? "0 6px 24px rgba(27,127,78,0.12)"
        : isBest && !isRejected
          ? "0 6px 24px rgba(231,164,50,0.15)"
          : "0 2px 10px rgba(22,50,68,0.06)",
      opacity: isRejected ? 0.55 : 1,
      transition: "all 0.2s",
      overflow: "hidden",
    }}>

      {/* Top label */}
      {isBest && !isRejected && !isAccepted && (
        <div style={{
          background: "#E7A432", padding: "5px 20px",
          fontSize: 11, fontWeight: 800, color: "#163244",
          letterSpacing: "0.08em",
        }}>
          ⭐ MEJOR PRECIO
        </div>
      )}
      {isAccepted && (
        <div style={{
          background: "#1b7f4e", padding: "5px 20px",
          fontSize: 11, fontWeight: 800, color: "#fff",
          letterSpacing: "0.08em",
        }}>
          ✓ PRESUPUESTO ACEPTADO
        </div>
      )}

      <div style={{ padding: "20px 22px" }}>
        {/* Provider info */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", marginBottom: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
              background: "#163244", display: "flex", alignItems: "center",
              justifyContent: "center", fontWeight: 800, fontSize: 18, color: "#E7A432",
            }}>
              {quote.provider_name[0]}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#163244", marginBottom: 2 }}>
                {quote.provider_name}
                {quote.provider_verified && (
                  <span style={{
                    marginLeft: 6, fontSize: 10, background: "#e7f0f5",
                    color: "#163244", padding: "1px 7px", borderRadius: 10, fontWeight: 700,
                  }}>
                    ✓ Verificado
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StarRating rating={quote.provider_rating} />
                <span style={{ fontSize: 11, color: "#8fa3b1" }}>
                  · {quote.provider_jobs} trabajos
                </span>
              </div>
            </div>
          </div>

          {/* Monto */}
          <div style={{ textAlign: "right" }}>
            <div style={{
              fontSize: 24, fontWeight: 800,
              color: isBest && !isRejected ? "#1b7f4e" : "#163244",
            }}>
              {formatARS(quote.amount)}
            </div>
            <div style={{ fontSize: 11, color: "#8fa3b1" }}>
              Recibido {timeAgo(quote.created_at)}
            </div>
          </div>
        </div>

        {/* Descripción */}
        <div style={{
          background: "#f7fafc", borderRadius: 8, padding: "12px 14px",
          fontSize: 13, color: "#163244", lineHeight: 1.7,
          borderLeft: "3px solid #cddae4", marginBottom: 12,
        }}>
          {quote.description}
        </div>

        {/* Chips de detalles */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {quote.estimated_start_date && (
            <div style={{
              background: "#f0f5f8", borderRadius: 6, padding: "5px 12px",
              fontSize: 12, color: "#163244", display: "flex", gap: 5,
            }}>
              📅 Inicio: <strong>{new Date(quote.estimated_start_date).toLocaleDateString("es-AR")}</strong>
            </div>
          )}
          <div style={{
            background: quote.estimated_visit_required ? "#fff3e0" : "#e8f7ef",
            borderRadius: 6, padding: "5px 12px",
            fontSize: 12, color: quote.estimated_visit_required ? "#b06a00" : "#1b7f4e",
          }}>
            {quote.estimated_visit_required ? "🔍 Requiere visita previa" : "✓ Sin visita previa"}
          </div>
          {remaining && !isAccepted && !isRejected && (
            <div style={{
              background: "#f0f5f8", borderRadius: 6, padding: "5px 12px",
              fontSize: 12, color: "#5c7a8a",
            }}>
              ⏳ Válido por {remaining}
            </div>
          )}
        </div>

        {/* Expand button */}
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 12, color: "#5c7a8a", fontWeight: 600, padding: 0,
            fontFamily: "Montserrat, sans-serif", marginBottom: expanded ? 12 : 0,
          }}
        >
          {expanded ? "▲ Ocultar" : "▼ Ver perfil del prestador"}
        </button>

        {expanded && (
          <div style={{
            background: "#f7fafc", borderRadius: 8, padding: "12px 14px",
            fontSize: 13, color: "#5c7a8a", marginBottom: 12,
          }}>
            <div style={{ fontWeight: 600, color: "#163244", marginBottom: 4 }}>
              {quote.provider_name}
            </div>
            <div>⭐ {quote.provider_rating} promedio · {quote.provider_jobs} trabajos completados</div>
            <div style={{ marginTop: 4 }}>🪪 Identidad verificada · 📱 Teléfono verificado</div>
          </div>
        )}

        {/* Actions */}
        {!isAccepted && !isRejected && (
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button
              onClick={() => onAccept(quote)}
              disabled={disabled || loading}
              style={{
                flex: 1, padding: "12px", borderRadius: 8, border: "none",
                background: disabled ? "#c8d4dc" : "#163244",
                color: "#fff", fontFamily: "Montserrat, sans-serif",
                fontWeight: 700, fontSize: 14,
                cursor: disabled || loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                transition: "background 0.2s",
              }}
            >
              {loading === `accept-${quote.id}` ? (
                <span style={{
                  width: 14, height: 14, border: "2px solid #fff",
                  borderTopColor: "transparent", borderRadius: "50%",
                  animation: "spin 0.7s linear infinite", display: "inline-block",
                }} />
              ) : "✓ Aceptar presupuesto"}
            </button>
            <button
              onClick={() => onReject(quote)}
              disabled={disabled || loading}
              style={{
                padding: "12px 18px", borderRadius: 8,
                border: "2px solid #e0e9ef", background: "transparent",
                color: "#5c7a8a", fontFamily: "Montserrat, sans-serif",
                fontWeight: 600, fontSize: 14,
                cursor: disabled || loading ? "not-allowed" : "pointer",
              }}
            >
              Rechazar
            </button>
          </div>
        )}

        {isRejected && (
          <div style={{
            fontSize: 12, color: "#c0392b", fontWeight: 600,
            padding: "8px 0 0",
          }}>
            ✕ Rechazaste este presupuesto
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientQuotesPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [request,  setRequest]  = useState(null);
  const [quotes,   setQuotes]   = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [modal, setModal]     = useState(null); // { quote, action }
  const [loading, setLoading] = useState(null);
  const [toast, setToast]     = useState(null);

  // Cargar solicitud y presupuestos desde el backend
  useEffect(() => {
    if (!requestId) return;
    setLoadingData(true);
    setLoadError("");
    api.get(`/matching/client/requests/${requestId}`)
      .then(res => {
        setRequest(res.data.request);
        setQuotes(res.data.quotes || []);
      })
      .catch(err => {
        console.error("Error cargando presupuestos:", err);
        setLoadError("No se pudieron cargar los presupuestos.");
      })
      .finally(() => setLoadingData(false));
  }, [requestId]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleAccept = (quote) => setModal({ quote, action: "accept" });
  const handleReject = (quote) => setModal({ quote, action: "reject" });

  const handleConfirm = async () => {
    const { quote, action } = modal;
    setModal(null);
    setLoading(`${action}-${quote.id}`);

    try {
      if (action === "accept") {
        await api.post(`/matching/client/quotes/${quote.id}/accept`);
        setQuotes(qs => qs.map(q =>
          q.id === quote.id
            ? { ...q, status: "accepted" }
            : q.status === "submitted" || q.status === "viewed"
              ? { ...q, status: "rejected" }
              : q
        ));
        // Actualizar status de la solicitud localmente
        setRequest(r => r ? { ...r, status: "assigned" } : r);
        showToast(`¡Contrataste a ${quote.provider_name}! Te contactará pronto.`);
      } else {
        await api.post(`/matching/client/quotes/${quote.id}/reject`);
        setQuotes(qs => qs.map(q =>
          q.id === quote.id ? { ...q, status: "rejected" } : q
        ));
        showToast("Presupuesto rechazado.", "info");
      }
    } catch (err) {
      showToast(err.response?.data?.error || "Ocurrió un error. Intentá de nuevo.", "error");
    } finally {
      setLoading(null);
    }
  };

  // Loading / error guards
  if (loadingData) return (
    <div style={{ minHeight: "100vh", background: "#f0f5f8", fontFamily: "Montserrat, sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#8fa3b1" }}>
        <span style={{ display: "inline-block", width: 28, height: 28, border: "3px solid #cddae4", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} />
        <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}>Cargando presupuestos...</div>
        <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      </div>
    </div>
  );

  if (loadError || !request) return (
    <div style={{ minHeight: "100vh", background: "#f0f5f8", fontFamily: "Montserrat, sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#c0392b", fontFamily: "Montserrat, sans-serif" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠</div>
        <div style={{ fontWeight: 600 }}>{loadError || "Solicitud no encontrada"}</div>
        <button onClick={() => navigate("/mis-solicitudes")} style={{ marginTop: 16, padding: "10px 20px", borderRadius: 8, background: "#163244", color: "#fff", border: "none", fontFamily: "Montserrat, sans-serif", fontWeight: 600, cursor: "pointer" }}>
          Volver a mis solicitudes
        </button>
      </div>
    </div>
  );

  const reqStatus = REQUEST_STATUS[request.status] || REQUEST_STATUS.waiting_quotes;
  const hasAccepted = quotes.some(q => q.status === "accepted");

  // Ordenar: pendientes primero por monto ASC, luego rechazados
  const sorted = [...quotes].sort((a, b) => {
    const order = { submitted: 0, viewed: 0, accepted: -1, rejected: 1 };
    if ((order[a.status] ?? 0) !== (order[b.status] ?? 0))
      return (order[a.status] ?? 0) - (order[b.status] ?? 0);
    return a.amount - b.amount;
  });

  const pending  = quotes.filter(q => ["submitted","viewed"].includes(q.status)).length;
  const cheapest = Math.min(...quotes.filter(q => !["rejected","expired"].includes(q.status)).map(q => q.amount));

  return (
    <div style={{ minHeight: "100vh", background: "#f0f5f8", fontFamily: "Montserrat, sans-serif" }}>

     
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "32px 20px 80px" }}>

        {/* Request summary card */}
        <div style={{
          background: "#163244", borderRadius: 14, padding: "22px 26px",
          marginBottom: 24, boxShadow: "0 4px 20px rgba(22,50,68,0.15)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <div style={{ color: "#8fa3b1", fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Tu solicitud
              </div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>
                {SERVICE_LABELS[request.service_key] || request.service_key}
              </div>
              <div style={{ color: "#8fa3b1", fontSize: 12, marginTop: 2 }}>
                📍 {request.common_data.address}
              </div>
            </div>
            <span style={{
              padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              color: reqStatus.color, background: reqStatus.bg,
            }}>
              {reqStatus.label.toUpperCase()}
            </span>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.07)", borderRadius: 8,
            padding: "10px 14px", fontSize: 13, color: "#c8d4dc", lineHeight: 1.6,
          }}>
            {request.common_data.description}
          </div>
        </div>

        {/* Stats bar */}
        {!hasAccepted && pending > 0 && (
          <div style={{
            background: "#fff", borderRadius: 12, padding: "14px 20px",
            marginBottom: 20, display: "flex", gap: 24, alignItems: "center",
            boxShadow: "0 2px 8px rgba(22,50,68,0.06)",
          }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#163244" }}>{pending}</div>
              <div style={{ fontSize: 11, color: "#8fa3b1", fontWeight: 600 }}>
                Presupuesto{pending !== 1 ? "s" : ""} recibido{pending !== 1 ? "s" : ""}
              </div>
            </div>
            <div style={{ width: 1, background: "#e0e9ef", alignSelf: "stretch" }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#1b7f4e" }}>
                {formatARS(cheapest)}
              </div>
              <div style={{ fontSize: 11, color: "#8fa3b1", fontWeight: 600 }}>Mejor precio</div>
            </div>
            <div style={{ marginLeft: "auto", fontSize: 13, color: "#5c7a8a" }}>
              Ordenados de menor a mayor precio ↓
            </div>
          </div>
        )}

        {/* Accepted confirmation */}
        {hasAccepted && (
          <div style={{
            background: "#e8f7ef", border: "2px solid #1b7f4e", borderRadius: 12,
            padding: "16px 20px", marginBottom: 20,
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%", background: "#1b7f4e",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22, flexShrink: 0,
            }}>
              ✓
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#1b7f4e", marginBottom: 2 }}>
                ¡Prestador contratado!
              </div>
              <div style={{ fontSize: 13, color: "#2e7d5e" }}>
                El prestador fue notificado y se comunicará con vos para coordinar el trabajo.
              </div>
            </div>
          </div>
        )}

        {/* Quotes header */}
        <div style={{ fontWeight: 700, fontSize: 15, color: "#163244", marginBottom: 14 }}>
          {hasAccepted ? "Presupuestos recibidos" : `${quotes.length} presupuestos para tu solicitud`}
        </div>

        {/* Quote cards */}
        {sorted.map((q, i) => {
          const rankAmongPending = sorted
            .filter(x => ["submitted","viewed"].includes(x.status))
            .indexOf(q);

          return (
            <QuoteCard
              key={q.id}
              quote={q}
              rank={rankAmongPending}
              onAccept={handleAccept}
              onReject={handleReject}
              disabled={hasAccepted}
              loading={loading}
            />
          );
        })}

        {quotes.length === 0 && (
          <div style={{
            textAlign: "center", padding: "48px 20px",
            background: "#fff", borderRadius: 14,
            border: "2px dashed #e0e9ef", color: "#8fa3b1",
          }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
              Esperando presupuestos
            </div>
            <div style={{ fontSize: 13 }}>
              Los prestadores tienen 48 horas para enviarte sus propuestas.
            </div>
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {modal && (
        <ConfirmModal
          quote={modal.quote}
          action={modal.action}
          onConfirm={handleConfirm}
          onClose={() => setModal(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10,
          fontWeight: 600, fontSize: 14, zIndex: 999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          borderLeft: "4px solid #E7A432", whiteSpace: "nowrap",
        }}>
          {toast.msg}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
